import { GridColDef, GridPaginationModel, GridRenderCellParams, GridTreeNodeWithRender } from '@mui/x-data-grid';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import CampaignStatusComponent from './CampaignStatusComponent';
import Utils from '../../components/Utils';
import { UserContext } from '../../App';
import { useContext, useEffect, useState } from 'react';
import CDGridPro from '../../components/CDGridPro';
import { Campaign, CampaignCriteria, CampaignPacing, CampaignStatus, Platform } from '../../types/Campaign';
import theme from '../../theme';
import {
    AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon,
    PublishedWithChangesOutlined,
} from '@mui/icons-material';
import Column from '../../components/Column';
import Row from '../../components/Row';
import { gridClasses } from '@mui/x-data-grid';
import { GridColumnVisibilityModel } from '@mui/x-data-grid/hooks/features/columns/gridColumnsInterfaces';
import CampaignType from '../../features/Campaign/CampaignType';
import './CampaignManagerGrid.css';

import CampaignManagerGridItemActions from './CampaignManagerGridItemActions';
import CampaignPacingUtils from '../../utils/CampaignPacingUtils';
import PlatformLogo from '../../components/Logo/PlatformLogo';
import { hasAccess } from '../../components/AccessGuard';
import { USER_GROUP_ADMIN } from '../../types/User';
import CampaignOemProgramValue from '../../features/Campaign/Values/CampaignOemProgramValue';
import ApiService from '../../ApiService';
import CampaignMediaType from '../../features/Campaign/CampaignMediaType';
import MediaTypeEditCell from '../../features/Campaign/MediaTypeEditCell';

function renderPercentage(params: GridRenderCellParams) {
    let pct = params.value;
    if (pct === null || pct === undefined) {
        return null;
    }

    let color = theme.palette.text.primary;
    pct = Utils.round(pct * 100, 0);

    switch (CampaignPacingUtils.identifyPacing(pct)) {
        case CampaignPacing.SLIGHTLY_BEHIND_SCHEDULE:
            color = theme.palette.warning.main;
            break;

        case CampaignPacing.ON_TRACK:
            color = theme.palette.success.main;
            break;

        default:
            color = theme.palette.error.main;
    }

    return (
        <span
            style={{
                fontWeight: 600,
                color: color,
            }}
        >
            {pct + '%'}
        </span>
    );
}

export default function CampaignManagerGrid(props: {
    campaigns: Campaign[];
    totalCampaigns: number;
    currentPage: number;
    pageSize: number;
    campaignCriteria: CampaignCriteria;
    deliveryStatusId: string;
    onPageChange(model: GridPaginationModel): void;
    onSortModelChange(sortModel: any): void;
    onShowDetails(campaign: Campaign): void;
    onGridColumnVisibilityChange(gridColumnVisibility: GridColumnVisibilityModel): void;
}) {
    const {
        campaigns,
        totalCampaigns,
        currentPage,
        pageSize,
        onPageChange,
        onSortModelChange,
        onGridColumnVisibilityChange,
        deliveryStatusId,
    } = props;
    const { userContext } = useContext(UserContext);
    const [gridColumnVisibility, setGridColumnVisibility] = useState<GridColumnVisibilityModel>({});

    useEffect(() => {
        onGridColumnVisibilityChange(gridColumnVisibility);
    }, [onGridColumnVisibilityChange, gridColumnVisibility]);

    const canAccessOemProgram = hasAccess(userContext, `${USER_GROUP_ADMIN}`);

    function canEditMediaType(campaign: Campaign): boolean {
        const isDraft = campaign.deliveryStatusId === 'DRAFT';
        const isPending = campaign.deliveryStatusId.startsWith('PENDING');
        const isAmazon = campaign.platform === Platform.AMAZON;
        return isAmazon ? isDraft || userContext.isSuperAdmin() : isPending || userContext.isAdmin();
    }

    const cellRenderer_OEMProgram = (params: GridRenderCellParams) => {
        const { row } = params;

        return <CampaignOemProgramValue campaign={row} />;
    };

    function cellRenderer_AdvertiserCampaign(params: GridRenderCellParams) {
        return (
            <div
                style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                <Column gap={0}>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {params.row.dealer.dealerName}
                    </Typography>
                    <Row spacing={0} gap={1}>
                        {params.row.hasUpdates && (
                            <Tooltip title="Pending Change Request">
                                <PublishedWithChangesOutlined color="warning" fontSize="small" />
                            </Tooltip>
                        )}
                        {userContext.isAdmin() ? (
                            <>
                                {params.row.dspCampaignId && params.row.platform === Platform.AMAZON ? (
                                    <Link
                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                        onClick={(event) => event.stopPropagation()}
                                        href={
                                            'https://advertising.amazon.com/dsp/ENTITY' +
                                            params.row.dspEntity.entityId.substring(1) +
                                            '/orders/' +
                                            params.row.dspCampaignId +
                                            '/edit'
                                        }
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {params.row.campaignName}
                                    </Link>
                                ) : (
                                    <Typography variant="body2">{params.row.campaignName}</Typography>
                                )}
                                <CampaignType
                                    iconOnly
                                    small
                                    mediaType={params.row.mediaType}
                                    campaignType={params.row.campaignType}
                                    strategyType={params.row.strategyType}
                                />
                            </>
                        ) : (
                            <>{params.row.campaignName}</>
                        )}
                    </Row>
                </Column>
            </div>
        );
    }

    useEffect(() => {
        let gridColumnVisibility: GridColumnVisibilityModel;
        // let columnsVisibility = localStorage.getItem('cmGridColumns_' + deliveryStatusId);
        let columnsVisibility = localStorage.getItem('cmGridColumns');
        if (columnsVisibility) {
            gridColumnVisibility = JSON.parse(columnsVisibility) as GridColumnVisibilityModel;
        } else {
            gridColumnVisibility = {
                agency: userContext.isAdmin() && deliveryStatusId === 'PENDING_COGNITION',
                createdByUser: deliveryStatusId === 'PENDING_COGNITION',
                createdDate: deliveryStatusId === 'PENDING_COGNITION',
                impressionGoal: false, // deliveryStatusId !== 'PENDING_COGNITION',
                impressionPacing: false, // deliveryStatusId !== 'PENDING_COGNITION',
                totalImpressions: deliveryStatusId !== 'PENDING_COGNITION',
                totalSpend: deliveryStatusId !== 'PENDING_COGNITION',
                budgetPacing: false, // deliveryStatusId !== 'PENDING_COGNITION',
            };
        }
        setGridColumnVisibility(gridColumnVisibility);
    }, [deliveryStatusId, userContext]);

    const columns: GridColDef[] = [
        {
            headerName: 'Platform',
            field: 'platform',
            width: 100,
            renderCell: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => {
                return <PlatformLogo size="Small" platform={params.value} />;
            },
            align: 'center',
            display: 'flex',
            hideable: true,
        },
        {
            headerName: 'Agency',
            field: 'agency',
            minWidth: 130,
            flex: 1,
            valueGetter: (value, row: { dealer: { agency: { name: string } } }) => {
                return row?.dealer?.agency?.name ?? '';
            },
            display: 'flex',
        },
        {
            headerName: 'User',
            field: 'createdByUser',
            minWidth: 120,
            flex: 1,
            valueGetter: (value, row: { createdByUser: { name: string } }) => {
                return row?.createdByUser?.name ?? '';
            },
            display: 'flex',
        },
        {
            field: 'createdDate',
            headerName: 'Create Date',
            minWidth: 130,
            flex: 1,
            valueGetter: (params: Date | null | undefined) => Utils.getDate(params),
            renderCell: (params) => {
                return Utils.formatESTDateLong(params.value);
            },
            display: 'flex',
        },
        {
            headerName: 'Advertiser / Campaign',
            field: 'dealerName',
            minWidth: 200,
            flex: 2,
            renderCell: cellRenderer_AdvertiserCampaign,
            disableColumnMenu: true,
            display: 'flex',
        },
        {
            headerName: 'Media Type',
            field: 'mediaType',
            minWidth: 130,
            flex: 1,
            display: 'flex',
            renderCell: (params: GridRenderCellParams) =>
                canEditMediaType(params.row) ? (
                    <MediaTypeEditCell
                        value={params.row.mediaType || ''}
                        onSave={(mediaType) => {
                            if (params.row.id) {
                                ApiService.updateCampaignMediaType(params.row.id, mediaType);
                            }
                        }}
                    />
                ) : (
                    <CampaignMediaType value={params.row.mediaType || ''} />
                ),
        },
        ...(canAccessOemProgram
            ? [
                  {
                      field: 'oemProgramName',
                      headerName: 'OEM Program',
                      renderHeader: () => (
                          <Row spacing={0} gap={1} alignItems="center">
                              <Typography variant="body2" fontWeight={600}>
                                  OEM Program
                              </Typography>
                              <Tooltip title="Visible to Admins Only">
                                  <AdminPanelSettingsOutlinedIcon color="primary" fontSize="small" />
                              </Tooltip>
                          </Row>
                      ),
                      minWidth: 170,
                      flex: 0,
                      renderCell: cellRenderer_OEMProgram,
                      display: 'flex',
                  } as GridColDef,
              ]
            : []),
        {
            headerName: 'Status',
            headerAlign: 'center',
            align: 'center',
            field: 'deliveryStatus',
            minWidth: deliveryStatusId === '' || deliveryStatusId === 'LINEITEMS_NOT_RUNNING' ? 170 : 100,
            flex: 1,
            valueGetter: (params: { internalStatus: string }) => {
                return params.internalStatus;
            },
            renderCell: (params: { row: { deliveryStatus: CampaignStatus } }) => {
                return <CampaignStatusComponent deliveryStatus={params.row.deliveryStatus} />;
            },
            disableColumnMenu: true,
            display: 'flex',
        },
        {
            field: 'startDate',
            headerName: 'Start Date',
            headerAlign: 'center',
            align: 'center',
            minWidth: 120,
            flex: 1,
            valueGetter: (params: Date | null | undefined) => Utils.getDate(params),
            renderCell: (params) => {
                return (
                    <span
                        style={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                        }}
                    >
                        {Utils.formatESTDateLong(params.value)}
                    </span>
                );
            },
            valueFormatter: (params: Date | null | undefined) => {
                return Utils.formatESTDateLong(params);
            },
            disableColumnMenu: true,
            display: 'flex',
        },
        {
            field: 'endDate',
            headerName: 'End Date',
            headerAlign: 'center',
            align: 'center',
            minWidth: 120,
            flex: 1,
            valueGetter: (params: Date | null | undefined) => Utils.getDate(params),
            valueFormatter: (params: Date | null | undefined) => {
                return Utils.formatESTDateLong(params);
            },
            renderCell: (params) => {
                return (
                    <span
                        style={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                        }}
                    >
                        {Utils.formatESTDateLong(params.value)}
                    </span>
                );
            },
            disableColumnMenu: true,
            display: 'flex',
        },
        {
            field: 'budget',
            type: 'number',
            headerName: 'Budget',
            headerAlign: 'right',
            align: 'right',
            minWidth: 90,
            renderCell: (params) => {
                return Utils.formatCurrency(params.value, 0);
            },
            disableColumnMenu: true,
            display: 'flex',
        },
        {
            field: 'impressionGoal',
            type: 'number',
            headerName: 'Impression Goal',
            description: 'Impression Goal',
            headerAlign: 'right',
            align: 'right',
            minWidth: 100,
            flex: 1,
            renderCell: (params) => {
                return params.value ? params.value.toLocaleString() : '';
            },
            disableColumnMenu: true,
            display: 'flex',
        },
        {
            field: 'totalSpend',
            type: 'number',
            headerName: 'Spend',
            description: 'Spend',
            headerAlign: 'right',
            align: 'right',
            minWidth: 90,
            flex: 1,
            disableColumnMenu: true,
            renderCell: (params) => {
                return Utils.formatCurrency(params.value, 2);
            },
            display: 'flex',
        },
        {
            field: 'totalImpressions',
            type: 'number',
            headerName: 'Impressions',
            description: 'Impressions',
            headerAlign: 'right',
            align: 'right',
            minWidth: 110,
            flex: 1,
            disableColumnMenu: true,
            renderCell: (params) => {
                return params.value === null ? null : params.value.toLocaleString();
            },
            display: 'flex',
        },
        {
            field: 'impressionPacing',
            type: 'number',
            headerName: 'Impression Pacing',
            description: 'Impression Pacing',
            headerAlign: 'right',
            align: 'right',
            minWidth: 100,
            flex: 1,
            disableColumnMenu: true,
            renderCell: renderPercentage,
            display: 'flex',
        },
        {
            field: 'budgetPacing',
            type: 'number',
            headerName: 'Budget Pacing',
            description: 'Budget Pacing',
            headerAlign: 'right',
            align: 'right',
            minWidth: 100,
            flex: 1,
            disableColumnMenu: true,
            renderCell: renderPercentage,
            display: 'flex',
        },
        {
            flex: 0.5,
            width: 50,
            field: 'actions',
            headerName: 'Actions',
            type: 'actions',
            renderCell: (params: { row: Campaign }) => {
                return <CampaignManagerGridItemActions campaign={params.row} />;
            },
            display: 'flex',
        },
    ];

    return (
        <>
            <Box
                className="campaign-manager-grid-box"
                sx={{
                    '& .hasUpdates': {
                        backgroundColor: theme.palette.warning.light,
                    },
                }}
            >
                <CDGridPro
                    showToolbar={false}
                    density="compact"
                    onRowClick={(params) => {
                        props.onShowDetails(params.row);
                    }}
                    onCellClick={(params, event) => {
                        if (params.field === 'mediaType' && canEditMediaType(params.row)) {
                            event.stopPropagation();
                        }
                    }}
                    paginationModel={{ page: currentPage, pageSize }}
                    columnVisibilityModel={gridColumnVisibility}
                    onColumnVisibilityModelChange={(params) => {
                        // localStorage.setItem('cmGridColumns_' + deliveryStatusId, JSON.stringify(params));
                        localStorage.setItem('cmGridColumns', JSON.stringify(params));
                        setGridColumnVisibility(params);
                    }}
                    onPaginationModelChange={onPageChange}
                    onSortModelChange={onSortModelChange}
                    columns={columns}
                    rows={campaigns}
                    rowCount={totalCampaigns}
                    paginationMode="server"
                    sortingMode="server"
                    rowHeight={95}
                    columnHeaderHeight={70}
                    sx={{
                        '& .cd--campaign-information': {
                            visibility: 'hidden',
                        },
                        '& .cd--campaign-monthly-information--general': {
                            [`& .${gridClasses.columnHeaderTitle}`]: {
                                visibility: 'hidden',
                            },
                        },
                    }}
                />
            </Box>
        </>
    );
}
